import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { CrewTemplateLogo } from '@/components/crews/crew-template-logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { getErrorMessage } from '@/lib/api'
import { ensureUniqueCrewTemplateCode } from '@/lib/crew-template-code'
import crewsApi from '@/services/crews'
import { workersApi } from '@/services/workers'
import type { Crew, CrewTemplate } from '@/types'

type StaffedCrewsPanelProps = {
  serviceInstanceId?: string | null
  focusTemplateId?: string | null
  onFocusHandled?: () => void
}

export function StaffedCrewsPanel({
  serviceInstanceId,
  focusTemplateId,
  onFocusHandled,
}: StaffedCrewsPanelProps) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [inlinePanelOpen, setInlinePanelOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [active, setActive] = useState(true)
  const [memberDrafts, setMemberDrafts] = useState<Record<string, string>>({})
  const [editingCrew, setEditingCrew] = useState<Crew | null>(null)
  const [templateFilterId, setTemplateFilterId] = useState<string | null>(null)
  const focusHandledRef = useRef(false)
  const codeManuallyEditedRef = useRef(false)

  const templatesQuery = useQuery({
    queryKey: ['crew-templates', serviceInstanceId],
    queryFn: () => crewsApi.listTemplates({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const crewsQuery = useQuery({
    queryKey: ['crews', serviceInstanceId],
    queryFn: () => crewsApi.listCrews({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const selectedTemplate = useMemo(
    () => (templatesQuery.data ?? []).find((template) => String(template.id) === selectedTemplateId) ?? null,
    [templatesQuery.data, selectedTemplateId],
  )

  const workerSkillsQuery = useQuery({
    queryKey: ['worker-skills', serviceInstanceId, selectedTemplate?.job_category],
    queryFn: () => workersApi.getWorkerSkills({
      service_instance_id: serviceInstanceId ?? undefined,
      job_category: selectedTemplate?.job_category ?? undefined,
      is_active: true,
    }),
    enabled: Boolean(serviceInstanceId && selectedTemplate),
  })

  const existingCrewCodes = useMemo(
    () => (crewsQuery.data ?? []).map((crew) => String(crew.code ?? '').trim()).filter(Boolean),
    [crewsQuery.data],
  )

  const buildCrewCodeSource = (teamName: string) => {
    const categoryPrefix = String(selectedTemplate?.job_category_name ?? '').trim()
    const normalizedTeamName = teamName.trim()
    if (!normalizedTeamName) {
      return ''
    }
    return categoryPrefix ? `${categoryPrefix}_${normalizedTeamName}` : normalizedTeamName
  }

  const suggestCrewCode = (value: string, excludeCode?: string | null) => (
    ensureUniqueCrewTemplateCode(buildCrewCodeSource(value), existingCrewCodes, excludeCode)
  )

  const handleNameChange = (value: string) => {
    setName(value)
    if (!codeManuallyEditedRef.current) {
      setCode(value.trim() ? suggestCrewCode(value, editingCrew?.code) : '')
    }
  }

  const handleCodeChange = (value: string) => {
    codeManuallyEditedRef.current = true
    setCode(value)
  }

  useEffect(() => {
    if (codeManuallyEditedRef.current) {
      return
    }
    setCode(name.trim() ? suggestCrewCode(name, editingCrew?.code) : '')
  }, [name, selectedTemplate?.job_category_name, editingCrew?.code])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!serviceInstanceId || !selectedTemplateId) {
        throw new Error('Team and service instance are required')
      }
      const resolvedCode = (code.trim() || suggestCrewCode(name.trim(), editingCrew?.code)).trim()
      if (!resolvedCode) {
        throw new Error('Team code is required')
      }
      const payload = {
        crew_template: selectedTemplateId,
        service_instance: serviceInstanceId,
        name: name.trim(),
        code: resolvedCode,
        active,
      }
      const crew = editingCrew?.id
        ? await crewsApi.updateCrew(editingCrew.id, payload)
        : await crewsApi.createCrew(payload)

      const existingMembersByKey = new Map(
        (editingCrew?.members ?? []).map((member) => [`${member.crew_template_slot}:${member.ordering}`, member]),
      )

      for (const slot of selectedTemplate?.slots ?? []) {
        const quantity = Number(slot.quantity ?? 1)
        for (let index = 1; index <= quantity; index += 1) {
          const key = `${slot.id}:${index}`
          const workerSkillId = memberDrafts[key]
          const existingMember = existingMembersByKey.get(key)

          if (!workerSkillId) {
            if (existingMember?.id) {
              await crewsApi.deleteMember(existingMember.id)
            }
            existingMembersByKey.delete(key)
            continue
          }

          if (existingMember?.id) {
            if (String(existingMember.worker_skill) !== workerSkillId) {
              await crewsApi.updateMember(existingMember.id, { worker_skill: workerSkillId })
            }
            existingMembersByKey.delete(key)
            continue
          }

          await crewsApi.createMember({
            crew: crew.id,
            crew_template_slot: slot.id!,
            worker_skill: workerSkillId,
            ordering: index,
          })
        }
      }

      for (const leftoverMember of existingMembersByKey.values()) {
        if (leftoverMember.id) {
          await crewsApi.deleteMember(leftoverMember.id)
        }
      }
      return crew
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crews'] })
      setDialogOpen(false)
      setInlinePanelOpen(false)
      toast.success(editingCrew ? 'Team updated' : 'Team created')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => crewsApi.deleteCrew(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crews'] })
      toast.success('Team deleted')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const loadCreateForm = (templateId?: string, defaults?: { name?: string; code?: string }) => {
    codeManuallyEditedRef.current = Boolean(defaults?.code)
    setEditingCrew(null)
    setSelectedTemplateId(templateId ?? '')
    const nextName = defaults?.name ?? ''
    setName(nextName)
    setCode(defaults?.code ?? (nextName.trim() ? suggestCrewCode(nextName) : ''))
    setActive(true)
    setMemberDrafts({})
  }

  const loadCrewForm = (crew: Crew) => {
    codeManuallyEditedRef.current = true
    setEditingCrew(crew)
    setSelectedTemplateId(String(crew.crew_template))
    setName(crew.name)
    setCode(crew.code)
    setActive(crew.active ?? true)
    const drafts: Record<string, string> = {}
    for (const member of crew.members ?? []) {
      drafts[`${member.crew_template_slot}:${member.ordering}`] = String(member.worker_skill)
    }
    setMemberDrafts(drafts)
  }

  const openCreate = (templateId?: string, defaults?: { name?: string; code?: string }) => {
    loadCreateForm(templateId, defaults)
    setDialogOpen(true)
  }

  const openEdit = (crew: Crew) => {
    loadCrewForm(crew)
    setDialogOpen(true)
  }

  const openInlineMembers = (templateId: string) => {
    const template = (templatesQuery.data ?? []).find((row) => String(row.id) === templateId)
    const templateCrews = (crewsQuery.data ?? []).filter(
      (crew) => String(crew.crew_template) === templateId,
    )

    setTemplateFilterId(templateId)

    if (templateCrews.length === 1) {
      loadCrewForm(templateCrews[0]!)
    } else if (templateCrews.length === 0) {
      loadCreateForm(templateId, { name: template?.name ?? '' })
    } else {
      const targetCrew = templateCrews.find((crew) => !crew.is_ready) ?? templateCrews[0]!
      loadCrewForm(targetCrew)
    }

    setInlinePanelOpen(true)
  }

  useEffect(() => {
    focusHandledRef.current = false
  }, [focusTemplateId])

  useEffect(() => {
    if (focusHandledRef.current || !focusTemplateId) {
      return
    }
    if (templatesQuery.isLoading || crewsQuery.isLoading) {
      return
    }

    openInlineMembers(focusTemplateId)
    focusHandledRef.current = true
    onFocusHandled?.()
  }, [
    crewsQuery.data,
    crewsQuery.isLoading,
    focusTemplateId,
    onFocusHandled,
    templatesQuery.data,
    templatesQuery.isLoading,
  ])

  const skillsForRole = (roleId: string | number) =>
    (workerSkillsQuery.data ?? []).filter((skill) => String(skill.skill_role) === String(roleId))

  const templateById = useMemo(
    () => new Map((templatesQuery.data ?? []).map((template) => [String(template.id), template])),
    [templatesQuery.data],
  )

  const visibleCrews = useMemo(() => {
    const crews = crewsQuery.data ?? []
    if (!templateFilterId) {
      return crews
    }
    return crews.filter((crew) => String(crew.crew_template) === templateFilterId)
  }, [crewsQuery.data, templateFilterId])

  const filteredTemplateName = templateFilterId ? templateById.get(templateFilterId)?.name : null
  const filteredTemplateCrews = useMemo(() => {
    if (!templateFilterId) {
      return []
    }
    return (crewsQuery.data ?? []).filter((crew) => String(crew.crew_template) === templateFilterId)
  }, [crewsQuery.data, templateFilterId])

  const renderTeamFormFields = () => (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Team</Label>
          <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId} disabled={Boolean(editingCrew) || inlinePanelOpen}>
            <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
            <SelectContent>
              {(templatesQuery.data ?? []).map((template: CrewTemplate) => (
                <SelectItem key={String(template.id)} value={String(template.id)}>{template.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Team name</Label>
          <Input value={name} onChange={(event) => handleNameChange(event.target.value)} placeholder="North Zone AC Team" />
        </div>
        <div className="space-y-2">
          <Label>Code</Label>
          <Input value={code} onChange={(event) => handleCodeChange(event.target.value)} placeholder="Auto-generated from name" />
        </div>
        <div className="flex items-center gap-2 pt-7">
          <Switch checked={active} onCheckedChange={setActive} />
          <Label>Active</Label>
        </div>
      </div>

      {selectedTemplate ? (
        <div className="space-y-3">
          <Label>Assign worker skills to slots</Label>
          {(selectedTemplate.slots ?? []).flatMap((slot) => {
            const quantity = Number(slot.quantity ?? 1)
            return Array.from({ length: quantity }, (_, index) => {
              const ordering = index + 1
              const key = `${slot.id}:${ordering}`
              return (
                <div key={key} className="grid gap-2 rounded-lg border p-3 md:grid-cols-[1fr_2fr]">
                  <div className="text-sm">
                    <p className="font-medium">{slot.slot_label || slot.skill_role_name}</p>
                    <p className="text-xs text-muted-foreground">Slot {ordering} of {quantity}</p>
                  </div>
                  <Select
                    value={memberDrafts[key] ?? ''}
                    onValueChange={(value) => setMemberDrafts((current) => ({ ...current, [key]: value }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select worker skill" /></SelectTrigger>
                    <SelectContent>
                      {skillsForRole(slot.skill_role).map((skill) => (
                        <SelectItem key={String(skill.id)} value={String(skill.id)}>
                          {(skill.worker_display_name ?? 'Worker')} • {skill.skill_role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )
            })
          })}
        </div>
      ) : null}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Staffed teams</h2>
          <p className="text-sm text-muted-foreground">Assign workers to role slots to build ready-to-deploy teams.</p>
        </div>
        <Button onClick={() => openCreate()} disabled={!serviceInstanceId}>
          <Plus className="mr-2 h-4 w-4" />
          New team
        </Button>
      </div>

      {filteredTemplateName ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Showing teams for</span>
          <span className="font-medium">{filteredTemplateName}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2"
            onClick={() => setTemplateFilterId(null)}
          >
            Show all
          </Button>
        </div>
      ) : null}

      {inlinePanelOpen ? (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>{editingCrew ? 'Edit team members' : 'Add team members'}</CardTitle>
                {filteredTemplateName ? (
                  <p className="mt-1 text-sm text-muted-foreground">{filteredTemplateName}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setInlinePanelOpen(false)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredTemplateCrews.length > 1 ? (
              <div className="space-y-2">
                <Label>Team</Label>
                <Select
                  value={editingCrew ? String(editingCrew.id) : ''}
                  onValueChange={(value) => {
                    const crew = filteredTemplateCrews.find((row) => String(row.id) === value)
                    if (crew) {
                      loadCrewForm(crew)
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {filteredTemplateCrews.map((crew) => (
                      <SelectItem key={String(crew.id)} value={String(crew.id)}>{crew.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {renderTeamFormFields()}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setInlinePanelOpen(false)}>Cancel</Button>
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim() || !selectedTemplateId}>
                Save team
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleCrews.map((crew) => {
          const template = templateById.get(String(crew.crew_template))
          return (
            <Card key={String(crew.id)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-3">
                    <CrewTemplateLogo
                      logoUrl={crew.crew_template_logo_url ?? template?.logo_url}
                      name={crew.crew_template_name ?? crew.name}
                    />
                    <div className="min-w-0">
                      <CardTitle className="text-base">{crew.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{crew.crew_template_name}</p>
                    </div>
                  </div>
                  <Badge variant={crew.is_ready ? 'default' : 'outline'}>{crew.is_ready ? 'Ready' : 'Incomplete'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1 text-xs text-muted-foreground">
                  {(crew.members ?? []).map((member) => (
                    <p key={String(member.id)}>
                      {member.slot_label || member.skill_role_name}: {member.worker_display_name}
                    </p>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(crew)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(crew.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingCrew ? 'Edit team' : 'Create team'}</DialogTitle>
          </DialogHeader>
          {renderTeamFormFields()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim() || !selectedTemplateId}>
              Save team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
